<script>
import Layout from "../layouts/main";
import PageHeader from "@/components/Page-header";

/**
 * Backend Account Management component
 */
export default {
  head () {
    return {
      title: `${this.title} | Minton - Nuxtjs Responsive Admin Dashboard Template`,
    };
  },
  data () {
    return {
      title: "後台帳號管理",
      items: [
        {
          text: "法語鸚鵡螺",
          href: "/",
        },
        {
          text: "後台帳號管理",
          active: true,
        }
      ],
      searchKeyword: "",
      currentPage: 1,
      totalRows: 0,
      perPage: 15,
      accounts: [],
      isLoading: false,
      isSaving: false,
      currentUserId: null, // 當前登入的管理員 ID
      // 角色選項
      roleOptions: [
        { value: "admin", label: "管理員" },
        { value: "super_admin", label: "總管理員" }
      ],
      // 權限選項
      permissionOptions: [
        { value: "add_game", label: "新增遊戲" },
        { value: "view_records", label: "用戶使用紀錄總表查看" },
        { value: "manage_accounts", label: "後台帳號管理" }
      ],
      // 當前編輯的帳號
      currentAccount: {
        id: null,
        account: "",
        role: "admin",
        status: true,
        permissions: [],
        password: "",
        confirmPassword: ""
      },
      isEditMode: false
    };
  },
  components: {
    Layout,
    PageHeader,
  },
  created () {
    this.getCurrentUser();
    this.getAccountList();
  },
  watch: {
    currentPage () {
      this.getAccountList();
    },
    // 監聽角色變化，自動更新權限
    'currentAccount.role' (newRole) {
      if (newRole === 'super_admin') {
        // 如果是總管理員，自動賦予所有權限
        this.currentAccount.permissions = this.permissionOptions.map(p => p.value);
      } else {
        // 如果從總管理員切換為普通管理員，清空權限（需要重新選擇）
        if (this.currentAccount.permissions.length === this.permissionOptions.length) {
          this.currentAccount.permissions = [];
        }
      }
    }
  },
  methods: {
    // 獲取當前登入的管理員資訊
    getCurrentUser () {
      const userCookie = this.$root.common.getCookie("user");
      if (userCookie) {
        try {
          // 如果已經是物件，直接使用
          let user;
          if (typeof userCookie === 'object') {
            user = userCookie;
          } else if (typeof userCookie === 'string') {
            user = JSON.parse(userCookie);
          } else {
            return;
          }
          this.currentUserId = user.id;
        } catch (e) {
          console.error("解析用戶資訊失敗:", e);
        }
      }
    },
    // 檢查是否為當前登入的管理員
    isCurrentUser (accountId) {
      return this.currentUserId && accountId === this.currentUserId;
    },
    // 獲取帳號列表
    getAccountList () {
      this.isLoading = true;

      const searchData = {
        search: this.searchKeyword,
        page: this.currentPage
      };

      this.$root.apis.getManagers(searchData, (response) => {
        this.isLoading = false;
        this.accounts = response.body.items || [];
        this.currentPage = response.body.page || 1;
        this.totalRows = response.body.total || 0;
        this.perPage = response.body.per_page || 15;
      }, (error) => {
        this.isLoading = false;
        console.error("獲取管理員列表失敗:", error);
        const message = error.body?.message || "獲取管理員列表失敗，請稍後再試";
        this.$root.common.showSingleNotify(message);
      });
    },
    // 搜尋
    handleSearch () {
      this.currentPage = 1;
      this.getAccountList();
    },
    // 打開新增模態框
    openAddModal () {
      this.isEditMode = false;
      this.currentAccount = {
        id: null,
        account: "",
        role: "admin",
        status: true,
        permissions: [],
        password: "",
        confirmPassword: ""
      };
      this.$bvModal.show("account-modal");
    },
    // 檢查是否為總管理員
    isSuperAdmin () {
      return this.currentAccount.role === 'super_admin';
    },
    // 打開編輯模態框
    openEditModal (account) {
      this.isEditMode = true;
      this.currentAccount = {
        id: account.id,
        account: account.account,
        role: account.role,
        status: account.status,
        permissions: [...account.permissions],
        password: "",
        confirmPassword: ""
      };
      this.$bvModal.show("account-modal");
    },
    // 保存帳號（新增或編輯）
    saveAccount () {
      // 驗證
      if (!this.currentAccount.account) {
        alert("請輸入帳號");
        return;
      }
      if (!this.isEditMode && !this.currentAccount.password) {
        alert("請輸入密碼");
        return;
      }
      // 密碼驗證（新增模式或編輯模式有輸入密碼時）
      if ((!this.isEditMode || this.currentAccount.password) && this.currentAccount.password !== this.currentAccount.confirmPassword) {
        alert("兩次輸入的密碼不一致");
        return;
      }
      // 權限驗證（僅非總管理員需要）
      if (this.currentAccount.role !== 'super_admin' && this.currentAccount.permissions.length === 0) {
        alert("請至少選擇一項權限");
        return;
      }

      // 如果是總管理員，自動賦予所有權限
      if (this.currentAccount.role === 'super_admin') {
        this.currentAccount.permissions = this.permissionOptions.map(p => p.value);
      }

      // 準備請求數據
      const requestData = {
        account: this.currentAccount.account,
        role: this.currentAccount.role,
        permissions: this.currentAccount.permissions
      };

      // 如果是編輯模式，加入狀態和密碼（如果提供）
      if (this.isEditMode) {
        requestData.status = this.currentAccount.status;
        if (this.currentAccount.password) {
          requestData.password = this.currentAccount.password;
          requestData.password_confirmation = this.currentAccount.confirmPassword;
        }
      } else {
        // 新增模式，必須提供密碼
        requestData.password = this.currentAccount.password;
        requestData.password_confirmation = this.currentAccount.confirmPassword;
      }

      this.isSaving = true;

      // 調用後端API
      if (this.isEditMode) {
        // 更新
        this.$root.apis.updateManager(this.currentAccount.id, requestData, (response) => {
          this.isSaving = false;
          if (response.body.status === 'ok') {
            this.$root.common.showSingleNotify('更新成功');
            this.$bvModal.hide("account-modal");
            this.getAccountList();
          } else {
            alert(response.body.message || "更新失敗");
          }
        }, (error) => {
          this.isSaving = false;
          console.error("更新管理員失敗:", error);
          const message = error.body?.message || "更新失敗，請稍後再試";
          alert(message);
        });
      } else {
        // 新增
        this.$root.apis.createManager(requestData, (response) => {
          this.isSaving = false;
          if (response.body.status === 'ok') {
            this.$root.common.showSingleNotify('新增成功');
            this.$bvModal.hide("account-modal");
            this.getAccountList();
          } else {
            alert(response.body.message || "新增失敗");
          }
        }, (error) => {
          this.isSaving = false;
          console.error("新增管理員失敗:", error);
          const message = error.body?.message || "新增失敗，請稍後再試";
          alert(message);
        });
      }
    },
    // 刪除帳號
    deleteAccount (account) {
      // 檢查是否為當前登入的管理員
      if (this.isCurrentUser(account.id)) {
        alert("無法刪除自己的帳號");
        return;
      }

      if (!confirm(`確認要刪除帳號 ${account.account} 嗎？`)) {
        return;
      }

      this.$root.apis.deleteManager(account.id, (response) => {
        if (response.body.status === 'ok') {
          this.$root.common.showSingleNotify('刪除成功');
          this.getAccountList();
        } else {
          alert(response.body.message || "刪除失敗");
        }
      }, (error) => {
        console.error("刪除管理員失敗:", error);
        const message = error.body?.message || "刪除失敗，請稍後再試";
        alert(message);
      });
    },
    // 切換狀態
    toggleStatus (account) {
      // 先保存原始狀態，以便失敗時恢復
      const originalStatus = account.status;

      this.$root.apis.toggleManagerStatus(account.id, (response) => {
        if (response.body.status === 'ok') {
          // 更新本地狀態
          account.status = response.body.data.status;
          this.$root.common.showSingleNotify('狀態更新成功');
        } else {
          // 恢復原狀態
          account.status = originalStatus;
          alert(response.body.message || "狀態更新失敗");
        }
      }, (error) => {
        // 恢復原狀態
        account.status = originalStatus;
        console.error("切換狀態失敗:", error);
        const message = error.body?.message || "狀態更新失敗，請稍後再試";
        alert(message);
      });
    },
    // 獲取角色標籤
    getRoleLabel (role) {
      const roleOption = this.roleOptions.find(r => r.value === role);
      return roleOption ? roleOption.label : role;
    },
    // 獲取權限文本
    getPermissionText (permissions, role) {
      // 如果是總管理員，顯示"全部權限"
      if (role === 'super_admin') {
        return '全部權限';
      }
      // 如果沒有權限，顯示"無權限"
      if (!permissions || permissions.length === 0) {
        return '無權限';
      }
      // 普通管理員顯示具體權限
      return permissions.map(perm => {
        const permOption = this.permissionOptions.find(p => p.value === perm);
        return permOption ? permOption.label : perm;
      }).join("、");
    },
    // 切換權限選擇
    togglePermission (permission) {
      const index = this.currentAccount.permissions.indexOf(permission);
      if (index > -1) {
        this.currentAccount.permissions.splice(index, 1);
      } else {
        this.currentAccount.permissions.push(permission);
      }
    },
    // 檢查權限是否選中
    isPermissionChecked (permission) {
      return this.currentAccount.permissions.includes(permission);
    }
  }
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <!-- 搜尋和新增按鈕區域 -->
            <div class="row mb-3">
              <div class="col-md-6">
                <div class="input-group">
                  <input type="text" class="form-control" placeholder="Search..." v-model="searchKeyword"
                    @keyup.enter="handleSearch" />
                  <div class="input-group-append">
                    <button class="btn btn-secondary" type="button" @click="handleSearch">
                      <i class="ri-search-line"></i>
                    </button>
                  </div>
                </div>
              </div>
              <div class="col-md-6 text-right">
                <button class="btn btn-dark" type="button" @click="openAddModal">
                  <i class="ri-add-line"></i> 新增帳號
                </button>
              </div>
            </div>

            <!-- 載入狀態 -->
            <div v-if="isLoading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="sr-only">載入中...</span>
              </div>
              <p class="mt-2 text-muted">載入中...</p>
            </div>

            <!-- 帳號列表表格 -->
            <div v-else class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="thead-light">
                  <tr>
                    <th class="text-center" style="width: 100px;">狀態</th>
                    <th>帳號</th>
                    <th>身份</th>
                    <th>權限管理</th>
                    <th class="text-center" style="width: 150px;">操作</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="account in accounts" :key="account.id">
                    <!-- 狀態列 -->
                    <td class="text-center">
                      <div class="custom-control custom-switch">
                        <input type="checkbox" class="custom-control-input" :id="`status-${account.id}`"
                          v-model="account.status" @change="toggleStatus(account)" />
                        <label class="custom-control-label" :for="`status-${account.id}`"></label>
                      </div>
                    </td>
                    <!-- 帳號列 -->
                    <td>{{ account.account }}</td>
                    <!-- 身份列 -->
                    <td>{{ getRoleLabel(account.role) }}</td>
                    <!-- 權限管理列 -->
                    <td>{{ getPermissionText(account.permissions, account.role) }}</td>
                    <!-- 操作列 -->
                    <td class="text-center">
                      <button class="btn btn-sm btn-light mr-1" @click="openEditModal(account)">
                        編輯
                      </button>
                      <button class="btn btn-sm btn-danger" @click="deleteAccount(account)"
                        :disabled="isCurrentUser(account.id)" :title="isCurrentUser(account.id) ? '無法刪除自己的帳號' : ''">
                        刪除
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- 分页 -->
            <div class="row mt-3">
              <div class="col-12">
                <div class="dataTables_paginate paging_simple_numbers float-right">
                  <ul class="pagination pagination-rounded">
                    <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage"></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 新增/編輯帳號模態框 -->
    <b-modal id="account-modal" :title="isEditMode ? '編輯' : '+ 新增帳號'" title-class="font-18" body-class="p-4" hide-footer
      size="md" header-bg-variant="dark" header-text-variant="light">
      <form>
        <!-- 狀態（僅編輯模式顯示） -->
        <div class="form-group" v-if="isEditMode">
          <label>狀態</label>
          <div class="custom-control custom-switch">
            <input type="checkbox" class="custom-control-input" id="edit-status" v-model="currentAccount.status" />
            <label class="custom-control-label" for="edit-status"></label>
          </div>
        </div>

        <!-- 帳號 -->
        <div class="form-group">
          <label>帳號</label>
          <input type="text" class="form-control" v-model="currentAccount.account" :disabled="isEditMode"
            placeholder="請輸入帳號" />
        </div>

        <!-- 身份 -->
        <div class="form-group">
          <label>身份</label>
          <select class="form-control" v-model="currentAccount.role">
            <option v-for="option in roleOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- 權限管理（僅非總管理員顯示） -->
        <div class="form-group" v-if="currentAccount.role !== 'super_admin'">
          <label>權限管理</label>
          <div class="mt-2">
            <div v-for="permission in permissionOptions" :key="permission.value"
              class="custom-control custom-checkbox mb-2">
              <input type="checkbox" class="custom-control-input" :id="`permission-${permission.value}`"
                :checked="isPermissionChecked(permission.value)" @change="togglePermission(permission.value)" />
              <label class="custom-control-label" :for="`permission-${permission.value}`">
                {{ permission.label }}
              </label>
            </div>
          </div>
        </div>

        <!-- 新密碼（僅編輯模式顯示） -->
        <div class="form-group" v-if="isEditMode">
          <label>新密碼</label>
          <input type="password" class="form-control" v-model="currentAccount.password" placeholder="留空則不修改密碼" />
        </div>

        <!-- 再次輸入新密碼（僅編輯模式顯示） -->
        <div class="form-group" v-if="isEditMode && currentAccount.password">
          <label>再次輸入新密碼</label>
          <input type="password" class="form-control" v-model="currentAccount.confirmPassword" placeholder="請再次輸入新密碼" />
        </div>

        <!-- 密碼（僅新增模式顯示） -->
        <div class="form-group" v-if="!isEditMode">
          <label>密碼</label>
          <input type="password" class="form-control" v-model="currentAccount.password" placeholder="請輸入密碼" />
        </div>

        <!-- 再次輸入密碼（僅新增模式顯示） -->
        <div class="form-group" v-if="!isEditMode">
          <label>再次輸入密碼</label>
          <input type="password" class="form-control" v-model="currentAccount.confirmPassword" placeholder="請再次輸入密碼" />
        </div>

        <!-- 按鈕 -->
        <div class="text-right pt-3">
          <button type="button" class="btn btn-secondary mr-2" @click="$bvModal.hide('account-modal')"
            :disabled="isSaving">
            取消
          </button>
          <button type="button" class="btn btn-primary" @click="saveAccount" :disabled="isSaving">
            <span v-if="isSaving" class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
            {{ isSaving ? '儲存中...' : (isEditMode ? '儲存' : '新增') }}
          </button>
        </div>
      </form>
    </b-modal>

  </Layout>
</template>
